'use client'

import { Model, PromptMode, User, ContentFile, Message } from "@/lib/types"
import { ModelId, models } from "@/lib/models/model-list"
import { defaultSettings } from "@/lib/user/default-user"
import { promptFormSchema } from "@/lib/types/prompt-area-types"
import { z } from "zod"
import { v4 as uuidv4 } from 'uuid'

// Draft management constants
////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////
const DRAFT_KEY_PREFIX = 'prompt_form_draft_'
const LAST_MODE_KEY = 'last_prompt_mode_used'

// Use the same dynamic type as the form
type DraftData = z.infer<typeof promptFormSchema[PromptMode]> & {
    timestamp: number
}

// Mode persistence functions
////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////
export function getLastModeUsed(): PromptMode | null {
    if (typeof window === 'undefined') return null
    
    try {
        const value = localStorage.getItem(LAST_MODE_KEY)
        return value ? value.replace(/^"|"$/g, '') as PromptMode : null
    } catch (error) {
        return null
    }
}

export function setLastModeUsed(mode: PromptMode) {
    if (typeof window === 'undefined') return
    
    try {
        localStorage.setItem(LAST_MODE_KEY, mode.replace(/^"|"$/g, ''))
    } catch (error) {
        // console.warn('Failed to set last mode to localStorage:', error)
    }
}

// Draft management helper functions (now per-mode)
////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////
export function saveDraftToStorage<T extends PromptMode>(
    mode: T,
    draft: z.infer<typeof promptFormSchema[T]>
) {
    if (typeof window === 'undefined') return
    
    try {
        // Don't save if form is essentially empty
        const isEmpty = !draft.prompt?.trim() && 
            (draft.mode === 'text' ? (!draft.files || draft.files.length === 0) : 
             draft.mode === 'image' ? !draft.sourceImage : true)
        
        if (isEmpty) {
            return
        }

        const draftWithTimestamp: DraftData = {
            ...draft,
            timestamp: Date.now()
        }
        
        // Use mode-specific key
        const draftKey = DRAFT_KEY_PREFIX + mode
        localStorage.setItem(draftKey, JSON.stringify(draftWithTimestamp))
    } catch (error) {
        // console.warn('Failed to save draft to localStorage:', error)
    }
}

export function deleteDraftFromStorage(mode: PromptMode) {
    if (typeof window === 'undefined') return
    
    try {
        const draftKey = DRAFT_KEY_PREFIX + mode
        localStorage.removeItem(draftKey)
    } catch (error) {
        // console.warn('Failed to delete draft from localStorage:', error)
    }
}


export function loadDraftFromStorage<T extends PromptMode>(
    mode: T
): z.infer<typeof promptFormSchema[T]> | null {
    if (typeof window === 'undefined') return null
    
    try {
        // Use mode-specific key
        const draftKey = DRAFT_KEY_PREFIX + mode
        const stored = localStorage.getItem(draftKey)
        
        if (!stored || stored === 'undefined' || stored === 'null' || stored.trim() === '') {
            return null
        }
        
        const draft = JSON.parse(stored) as DraftData
        
        // Validate the draft against the current schema
        const currentSchema = promptFormSchema[mode]
        const validationResult = currentSchema.safeParse(draft)
        
        if (!validationResult.success) {
            // console.warn(`Draft failed validation for ${mode} mode, ignoring:`, validationResult.error)
            return null
        }
        
        return validationResult.data
    } catch (error) {
        // console.warn('Failed to load draft from localStorage:', error)
        return null
    }
}

// Helper functions for managing lastModelUsed by media type
export function getLastModelUsed(mediaType: PromptMode): ModelId | null {
    if (typeof window === 'undefined') return null
    
    const key = `lastModelUsed_${mediaType}`
    const value = localStorage.getItem(key)
    // Strip any extra quotes from the stored value
    return value ? value.replace(/^"|"$/g, '') as ModelId : null
}

export function setLastModelUsed(mediaType: PromptMode, modelId: ModelId) {
    if (typeof window === 'undefined') return
    
    const key = `lastModelUsed_${mediaType}`
    // Ensure we're not storing with quotes
    localStorage.setItem(key, modelId.replace(/^"|"$/g, ''))
}

export function getModelFallback(modelId: string): Model {
    const visitedModels = new Set<string>() // Prevent infinite loops
    let currentModelId = modelId as ModelId
    
    while (currentModelId && !visitedModels.has(currentModelId)) {
        visitedModels.add(currentModelId)
        const model = models[currentModelId]
        
        if (!model) {
            // Model doesn't exist, break out of loop
            break
        }
        
        // Check if model is active and not deprecated/sunset
        if (model.active && (model.status === 'active' || model.status === 'preview')) {
            return model
        }
        
        // Model is inactive/deprecated, try its fallback
        if (model.fallbackModelId) {
            // console.warn(`Model ${currentModelId} is ${model.status}, using fallback: ${model.fallbackModelId}`)
            currentModelId = model.fallbackModelId
            continue
        }
        
        // No fallback specified, break out
        break
    }
    
    // If we get here, the fallback chain failed
    // Try the default model as last resort
    const defaultModel = models[defaultSettings.defaultTextModel]
    if (defaultModel && defaultModel.active && (defaultModel.status === 'active' || defaultModel.status === 'preview')) {
        // console.warn(`Model ${modelId} and its fallbacks are not available, using default: ${defaultSettings.defaultTextModel}`)
        return defaultModel
    }
    
    // Even default model is not working, find first active model
    const firstActiveModel = Object.values(models).find(model => 
        model.active && (model.status === 'active' || model.status === 'preview')
    )
    
    if (firstActiveModel) {
        console.error(`Default model ${defaultSettings.defaultTextModel} not available, using first active model: ${firstActiveModel.id}`)
        return firstActiveModel
    }
    
    // This should never happen, but provide a final fallback
    throw new Error('No active models available in the models list')
}

export function getDefaultModel(user: User | null, currentMode: PromptMode): Model {
    const userSettings = user?.settings || defaultSettings
    const modelToLoad = userSettings.modelToLoad || "default"
    
    // For SSR compatibility, always return default model when "last-used" is selected
    // The actual last-used model will be set client-side in usePromptForm
    if (modelToLoad === "last-used") {
        const defaultModelId = currentMode === 'image' ? userSettings.defaultImageModel : userSettings.defaultTextModel
        return getModelFallback(defaultModelId)
    }
    
    // If user prefers "default", always return the default model
    if (modelToLoad === "default") {
        const defaultModelId = currentMode === 'image' ? userSettings.defaultImageModel : userSettings.defaultTextModel
        return getModelFallback(defaultModelId)
    }
    
    // Fallback for any unexpected values
    const defaultModelId = currentMode === 'image' ? userSettings.defaultImageModel : userSettings.defaultTextModel
    return getModelFallback(defaultModelId)
}



export function buildNewUserMessage({
    data,
    parentId
}: {
    data: z.infer<typeof promptFormSchema[PromptMode]>, 
    parentId?: string | null
}): Message {
    console.log('buildNewUserMessage', data)
    // Build files array from either text files or image sourceImage
    const files: ContentFile[] = []
    if ('files' in data && Array.isArray((data as any).files)) {
        files.push(...((data as any).files as ContentFile[]))
    }
    if ('sourceImage' in data && (data as any).sourceImage) {
        const src = (data as any).sourceImage as ContentFile
        files.push(src)
    }
    return {
        id: uuidv4(),
        content: data.prompt,
        modelId: data.model.id,
        role: "user",
        createdAt: new Date(),
        parentId: parentId || null,
        info: {
            type: "text",
            isBookmarked: false,
            voiceMode: false,
            status: { isLoading: false }
        },
        files: files,
        // parameters: data.parameters || {},
        error: undefined
    }
}